/**
 * Thread owner — the discriminator that keeps a lead's thread and a matter's
 * thread apart (LF-6001 / LEADS-34).
 *
 * A conversation thread hangs off either a matter or a lead. Both ids come
 * from separate auto-increment columns, so `42` is an entirely different
 * conversation depending on which one it is. Every cache key and every
 * client-side scoping string for a thread must therefore carry the owner
 * *type* alongside the id — an id alone is ambiguous.
 *
 * This is not cosmetic. Before this module the owner id sat positionally in
 * the query-key tuple and in `threadKey`, and `threadKey` seeds a
 * **localStorage** draft key — so lead 42 and matter 42 shared one persisted
 * composer draft, and a lead's draft body surfaced inside a matter's
 * composer across sessions.
 *
 * Deliberately dependency-free: `lib/query-keys/communications.ts` imports
 * this, and that module is itself imported by `query-dependencies.ts`. Adding
 * an import here risks a cycle.
 */

/**
 * Which entity a communications thread belongs to.
 *
 * A discriminated union rather than `{ matterId?, leadId? }` so the
 * "exactly one owner" invariant is enforced by the type system instead of by
 * convention — the illegal both-set and neither-set states are unrepresentable.
 */
export type ThreadOwner =
  | { readonly type: 'matter'; readonly matterId: number }
  | { readonly type: 'lead'; readonly leadId: number };

/** A matter-owned thread. */
export const matterOwner = (matterId: number): ThreadOwner => ({
  type: 'matter',
  matterId
});

/** A lead-owned thread. */
export const leadOwner = (leadId: number): ThreadOwner => ({
  type: 'lead',
  leadId
});

/** The owner's id, whichever kind it is. */
export function threadOwnerId(owner: ThreadOwner): number {
  return owner.type === 'matter' ? owner.matterId : owner.leadId;
}

/**
 * The owner's contribution to a query key, as **positional segments** —
 * `['matter', 42]` / `['lead', 42]`.
 *
 * Positional segments, not a nested `{ type, id }` object, because React
 * Query invalidation is prefix-based and several callers rely on that: they
 * invalidate *every* participant thread for one owner by passing a key that
 * stops short of `participantType` / `participantId` (see
 * `communicationsKeys.threadItemsForOwner`). Positional segments extend that
 * existing prefix cleanly; an object at index 2 would make the owner segment
 * match by (partial) deep equality instead, which is a subtler rule than the
 * rest of this codebase's key factories use.
 */
export function threadOwnerSegments(
  owner: ThreadOwner
): readonly ['matter', number] | readonly ['lead', number] {
  return owner.type === 'matter'
    ? (['matter', owner.matterId] as const)
    : (['lead', owner.leadId] as const);
}

/**
 * The owner as a single namespaced string token — `matter:42` / `lead:42`.
 *
 * For the string-keyed (non-query-key) scoping surfaces: the mark-read dedupe
 * Set, the composer remount key, and the localStorage draft key.
 */
export function threadOwnerToken(owner: ThreadOwner): string {
  return `${owner.type}:${threadOwnerId(owner)}`;
}

/**
 * The client-side identity of one thread — `matter:42:contact:101`.
 *
 * Used as the composer remount `key`, the base of the mark-read dedupe key,
 * and the `draftStorageKey` that scopes the composer's localStorage draft.
 * The owner token leads, so no lead/matter pair can collide.
 */
export function buildThreadKey(
  owner: ThreadOwner,
  participantType: string,
  participantId: number
): string {
  return `${threadOwnerToken(owner)}:${participantType}:${participantId}`;
}
